// How the storefront turns a product's variants into pickers, prices and pictures.
// No DOM and no imports here: the product page renders this on the server, the same
// functions answer every later choice in the browser, and the header's bag count can
// load it on every page without pulling in the schema library.

/** The most of one item a single order may carry (the cart contract allows 20). */
export const lineLimit = 20;

export type CatalogVariant = {
  id: string;
  title: string;
  priceCents: number;
  compareAtPriceCents?: number;
  stock: number | null;
  options?: readonly { name: string; value: string }[];
  swatch?: string;
  imageId?: string;
};
export type CatalogProduct = {
  title: string;
  imageIds: readonly string[];
  variants: readonly CatalogVariant[];
};
export type PickerValue = { value: string; label: string; swatch: string | null };
export type PickerGroup = { label: string; swatches: boolean; values: PickerValue[] };
export type PickerVariant = {
  id: string;
  title: string;
  priceCents: number;
  compareAtPriceCents?: number;
  // How many can be bought now: 0 when sold out, never more than one order line allows.
  max: number;
  // Position of this variant's picture among the product images, or -1.
  image: number;
  // The chosen value in each group, in group order.
  values: string[];
};
export type Picker = { groups: PickerGroup[]; variants: PickerVariant[] };
export type Availability = "available" | "soldOut" | "missing";

const hex = /^#[0-9a-f]{6}$/i;
const colorName = /colou?r/i;
const sizeName = /size/i;
const garmentSizes = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL"];

// Garment sizes in wearing order (the order `sortMerchSizes` gives the dashboard);
// anything else keeps the order the product lists it in.
function wearingOrder(sizes: readonly string[]) {
  const rank = (size: string) => {
    const index = garmentSizes.indexOf(size.toUpperCase());
    return index === -1 ? garmentSizes.length : index;
  };
  return sizes
    .map((size, index) => ({ size, index }))
    .sort((a, b) => rank(a.size) - rank(b.size) || a.index - b.index)
    .map((item) => item.size);
}

export function availableQuantity(variant: Pick<CatalogVariant, "stock">) {
  return variant.stock === null ? lineLimit : Math.max(0, Math.min(lineLimit, variant.stock));
}

// Pickers need every variant to answer the same questions, each combination once.
// Anything else (imports, older products) is offered as one list of variant titles.
function optionNames(variants: readonly CatalogVariant[]) {
  const names = variants[0]?.options?.map((option) => option.name) ?? [];
  if (!names.length) return null;
  const seen = new Set<string>();
  for (const variant of variants) {
    const options = variant.options ?? [];
    if (
      options.length !== names.length ||
      options.some((option, index) => option.name !== names[index])
    )
      return null;
    const key = JSON.stringify(options.map((option) => option.value));
    if (seen.has(key)) return null;
    seen.add(key);
  }
  return names;
}

// The colour of each value in a group, when every value has exactly one.
function groupSwatches(variants: readonly CatalogVariant[], group: number) {
  const swatches = new Map<string, Set<string>>();
  for (const variant of variants) {
    const value = variant.options![group]!.value;
    const found = swatches.get(value) ?? new Set<string>();
    if (variant.swatch && hex.test(variant.swatch)) found.add(variant.swatch.toLowerCase());
    swatches.set(value, found);
  }
  const single = new Map<string, string>();
  for (const [value, found] of swatches) {
    if (found.size !== 1) return null;
    single.set(value, [...found][0]!);
  }
  return single;
}

export function buildPicker(product: Pick<CatalogProduct, "imageIds" | "variants">): Picker {
  const base = (variant: CatalogVariant, values: string[]): PickerVariant => ({
    id: variant.id,
    title: variant.title,
    priceCents: variant.priceCents,
    ...(variant.compareAtPriceCents === undefined
      ? {}
      : { compareAtPriceCents: variant.compareAtPriceCents }),
    max: availableQuantity(variant),
    image: variant.imageId ? product.imageIds.indexOf(variant.imageId) : -1,
    values,
  });
  if (product.variants.length < 2)
    return { groups: [], variants: product.variants.map((variant) => base(variant, [])) };
  const names = optionNames(product.variants);
  if (!names)
    return {
      groups: [
        {
          label: "Option",
          swatches: false,
          values: product.variants.map((variant) => ({
            value: variant.id,
            label: variant.title,
            swatch: null,
          })),
        },
      ],
      variants: product.variants.map((variant) => base(variant, [variant.id])),
    };
  const swatches = names.map((_, group) => groupSwatches(product.variants, group));
  // Sizes of a single-colour product all share that colour, so the group named for
  // colour gets the swatches. Without such a name, it is the group with a colour per value.
  const named = names.findIndex((name) => colorName.test(name));
  const swatchGroup =
    named !== -1
      ? swatches[named]
        ? named
        : -1
      : swatches.findIndex((found) => found && new Set(found.values()).size === found.size);
  return {
    groups: names.map((name, group) => {
      const seen = [...new Set(product.variants.map((variant) => variant.options![group]!.value))];
      const ordered = sizeName.test(name) ? wearingOrder(seen) : seen;
      return {
        label: name,
        swatches: group === swatchGroup,
        values: ordered.map((value) => ({
          value,
          label: value,
          swatch: group === swatchGroup ? swatches[group]!.get(value)! : null,
        })),
      };
    }),
    variants: product.variants.map((variant) =>
      base(
        variant,
        variant.options!.map((option) => option.value),
      ),
    ),
  };
}

export function findVariant(picker: Picker, values: readonly string[]) {
  return picker.variants.find((variant) =>
    variant.values.every((value, group) => value === values[group]),
  );
}

/** The first combination that can be bought, so the page opens on something available. */
export function defaultValues(picker: Picker) {
  return (picker.variants.find((variant) => variant.max > 0) ?? picker.variants[0])?.values ?? [];
}

// The picture a product page opens on. Normally the first; when the first picture belongs
// to a choice other than the one the page opens with (its colour is sold out), the opening
// choice's own picture. A first picture that belongs to no variant always stays.
export function openingImage(picker: Picker) {
  const image = findVariant(picker, defaultValues(picker))?.image ?? -1;
  return image > 0 && picker.variants.some((variant) => variant.image === 0) ? image : 0;
}

// Whether choosing `value` can still lead to a purchase, given the choices made in the
// groups before it. Later groups are not counted: people choose a colour, then a size.
export function valueAvailability(
  picker: Picker,
  values: readonly string[],
  group: number,
  value: string,
): Availability {
  const matches = picker.variants.filter(
    (variant) =>
      variant.values[group] === value &&
      variant.values.slice(0, group).every((chosen, index) => chosen === values[index]),
  );
  if (!matches.length) return "missing";
  return matches.some((variant) => variant.max > 0) ? "available" : "soldOut";
}

export function selectionAvailability(picker: Picker, values: readonly string[]) {
  const variant = findVariant(picker, values);
  const state: Availability = !variant ? "missing" : variant.max > 0 ? "available" : "soldOut";
  return { variant, state };
}

// The picture for a selection: its own, or one that shares the choice just made
// (a colour keeps its picture when the chosen size has none or does not exist).
export function selectionImage(picker: Picker, values: readonly string[], changed: number) {
  const exact = findVariant(picker, values);
  if (exact && exact.image >= 0) return exact.image;
  const sharing = picker.variants.find(
    (variant) =>
      variant.image >= 0 &&
      variant.values.slice(0, changed + 1).every((value, index) => value === values[index]),
  );
  return sharing ? sharing.image : -1;
}

/** The current choices in words ("Navy / M"), also when no variant matches them. */
export function choiceLabel(picker: Picker, values: readonly string[]) {
  return picker.groups
    .map((group, index) => group.values.find((item) => item.value === values[index])?.label)
    .filter(Boolean)
    .join(" / ");
}

/** What was chosen, for a bag line or an order ("Navy / M"). Empty for a single variant. */
export function variantLabel(product: Pick<CatalogProduct, "variants">, variant: CatalogVariant) {
  if (product.variants.length < 2) return "";
  return optionNames(product.variants)
    ? variant.options!.map((option) => option.value).join(" / ")
    : variant.title;
}

export function variantImage(
  product: Pick<CatalogProduct, "imageIds">,
  variant: Pick<CatalogVariant, "imageId"> | null | undefined,
) {
  const index = variant?.imageId ? product.imageIds.indexOf(variant.imageId) : -1;
  return index >= 0 ? index : product.imageIds.length ? 0 : -1;
}

// Alt text for each product image. A picture that belongs to one choice says which
// ("Tee, Navy"); the rest are numbered so they can be told apart.
export function imageAlts(product: CatalogProduct, picker = buildPicker(product)) {
  const total = product.imageIds.length;
  return product.imageIds.map((_, index) => {
    const shown = picker.variants.filter((variant) => variant.image === index);
    const shared = picker.groups.flatMap((group, position) => {
      if (group.values.length < 2 || !shown.length) return [];
      const value = shown[0]!.values[position]!;
      if (shown.some((variant) => variant.values[position] !== value)) return [];
      return [group.values.find((item) => item.value === value)?.label ?? value];
    });
    if (shared.length) return `${product.title}, ${shared.join(", ")}`;
    return total > 1 ? `${product.title}, image ${index + 1} of ${total}` : product.title;
  });
}

/** Colour dots for a product card, and how many colours there are in all. */
export function cardSwatches(picker: Picker, limit = 4) {
  const group = picker.groups.find((item) => item.swatches);
  if (!group || group.values.length < 2) return null;
  return {
    count: group.values.length,
    shown: group.values
      .slice(0, limit)
      .map((item) => ({ label: item.label, swatch: item.swatch! })),
  };
}

export function priceRange(variants: readonly Pick<CatalogVariant, "priceCents">[]) {
  const prices = variants.map((variant) => variant.priceCents);
  return { low: Math.min(...prices), high: Math.max(...prices) };
}
